import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Papa from "papaparse";
import {
  Upload,
  CheckCircle2,
  AlertCircle,
  X,
  Download,
  Loader2,
  Sparkles,
  Trash2,
} from "lucide-react";

const SCHEMA_FIELDS = [
  { key: "name", labelKey: "csv.fields.name", label: "Name", type: "string", required: true },
  { key: "categoryId", labelKey: "csv.fields.categoryId", label: "Category ID", type: "int", required: true },
  { key: "price", labelKey: "csv.fields.price", label: "Price", type: "float", required: true },
  { key: "origin", labelKey: "csv.fields.origin", label: "Origin", type: "string", required: false },
  { key: "tag", labelKey: "csv.fields.tag", label: "Tag", type: "string", required: false },
  { key: "imageUrl", labelKey: "csv.fields.imageUrl", label: "Image URL", type: "string", required: false },
  { key: "description", labelKey: "csv.fields.description", label: "Description", type: "string", required: false },
  { key: "rating", labelKey: "csv.fields.rating", label: "Rating", type: "float", required: false },
];

const IGNORE = "__ignore__";
const fieldLabel = (field, t) => t(field.labelKey, { defaultValue: field.label });

function coerce(raw, type) {
  if (raw === undefined || raw === null) return null;
  const v = String(raw).trim();
  if (v === "") return null;
  if (type === "int") {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : NaN;
  }
  if (type === "float") {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  }
  return v;
}

function guessMapping(header) {
  const h = header.toLowerCase().replace(/[\s_-]/g, "");
  const aliases = {
    name: ["name", "title", "productname"],
    categoryId: ["categoryid", "category", "catid", "cat"],
    price: ["price", "cost", "amount"],
    origin: ["origin", "orig", "country", "madein"],
    tag: ["tag", "tags", "label", "badge"],
    imageUrl: ["imageurl", "img", "image", "picture", "photo", "thumbnail"],
    description: ["description", "desc", "details", "about"],
    rating: ["rating", "rate", "stars", "score"],
  };
  for (const [field, list] of Object.entries(aliases)) {
    if (list.some((a) => a === h)) return field;
  }
  return IGNORE;
}

function buildProduct(row, mapping, t) {
  const product = {};
  const errors = [];

  for (const field of SCHEMA_FIELDS) {
    const sourceCol = Object.entries(mapping).find(([, mapped]) => mapped === field.key)?.[0];
    const label = fieldLabel(field, t);

    if (!sourceCol) {
      if (field.required) errors.push(t("csv.not_mapped", { field: label }));
      continue;
    }

    const val = coerce(row[sourceCol], field.type);
    if (val === null) {
      if (field.required) errors.push(t("csv.is_empty", { field: label }));
      continue;
    }
    if (Number.isNaN(val)) {
      errors.push(t("csv.not_valid", { field: label, type: field.type }));
      continue;
    }
    product[field.key] = val;
  }

  return { product, errors };
}

export default function ProductCsvImporter({ endpoint = "/api/products", onImported }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const preview = useMemo(() => {
    if (!rows.length) return { valid: [], invalid: [] };
    const valid = [];
    const invalid = [];
    rows.forEach((row, idx) => {
      const { product, errors } = buildProduct(row, mapping, t);
      if (errors.length) invalid.push({ idx, product, errors, row });
      else valid.push({ idx, product, row });
    });
    return { valid, invalid };
  }, [rows, mapping, t]);

  const unmappedRequired = SCHEMA_FIELDS.filter(
    (field) => field.required && !Object.values(mapping).includes(field.key)
  );

  function handleFile(f) {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      alert(t("csv.upload_csv_alert"));
      return;
    }

    setFile(f);
    setParsing(true);
    setResults(null);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const hdrs = res.meta.fields || [];
        setHeaders(hdrs);
        setRows(res.data);
        setMapping(Object.fromEntries(hdrs.map((h) => [h, guessMapping(h)])));
        setParsing(false);
      },
      error: (err) => {
        alert(t("csv.parse_error", { message: err.message }));
        setParsing(false);
      },
    });
  }

  function reset() {
    setFile(null);
    setRows([]);
    setHeaders([]);
    setMapping({});
    setResults(null);
    setProgress({ done: 0, total: 0 });
    if (inputRef.current) inputRef.current.value = "";
  }

  async function runImport() {
    if (!preview.valid.length) return;
    setImporting(true);
    setProgress({ done: 0, total: preview.valid.length });
    const success = [];
    const failed = [];

    for (let i = 0; i < preview.valid.length; i += 1) {
      const { product, idx } = preview.valid[i];
      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(product),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          failed.push({ idx, product, error: `HTTP ${res.status} ${text}` });
        } else {
          const data = await res.json().catch(() => ({}));
          success.push({ idx, product, response: data });
        }
      } catch (err) {
        failed.push({ idx, product, error: err.message || t("csv.network_error") });
      }
      setProgress({ done: i + 1, total: preview.valid.length });
    }

    setResults({ success, failed });
    setImporting(false);
    onImported?.({ success, failed });
  }

  function downloadTemplate() {
    const csv = [
      SCHEMA_FIELDS.map((field) => field.key).join(","),
      `"Sample Product",1,29.99,"France","featured","https://example.com/img.jpg","A nice product",4.5`,
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8 pb-6 border-b border-stone-200">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              <span className="text-xs uppercase tracking-widest text-stone-500 font-medium">
                {t("csv.bulk_import")}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{t("csv.title")}</h1>
            <p className="text-stone-600 mt-2 text-sm">{t("csv.subtitle")}</p>
          </div>
          <button
            onClick={downloadTemplate}
            className="hidden md:inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-stone-300 hover:border-stone-900 hover:bg-stone-900 hover:text-white transition-colors"
          >
            <Download className="w-4 h-4" />
            {t("csv.template")}
          </button>
        </div>

        {!file && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all ${
              dragActive ? "border-emerald-600 bg-emerald-50" : "border-stone-300 bg-white hover:border-stone-900"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Upload className="w-12 h-12 mx-auto text-stone-400 mb-4" />
            <p className="text-lg font-medium">{t("csv.drop_here")}</p>
            <p className="text-sm text-stone-500 mt-1">{t("csv.browse_hint")}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadTemplate();
              }}
              className="mt-6 text-xs text-emerald-700 hover:text-emerald-900 underline"
            >
              {t("csv.download_template")}
            </button>
          </div>
        )}

        {file && parsing && (
          <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-stone-400" />
            <p className="mt-4 text-sm text-stone-600">{t("csv.parsing")}</p>
          </div>
        )}

        {file && !parsing && !results && (
          <div className="space-y-6">
            <div className="bg-white border border-stone-200 rounded-2xl p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-xs text-stone-500">{rows.length} {t("common.items")}</p>
                </div>
              </div>
              <button onClick={reset} title={t("csv.remove_file")} className="p-2 rounded-lg hover:bg-stone-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl p-6">
              <h2 className="font-semibold mb-1">{t("csv.map_columns")}</h2>
              <p className="text-sm text-stone-500 mb-5">{t("csv.subtitle")}</p>

              <div className="grid md:grid-cols-2 gap-3">
                {headers.map((h) => (
                  <div key={h} className="flex items-center justify-between gap-3 border border-stone-200 rounded-lg p-3">
                    <div className="min-w-0">
                      <p className="text-xs text-stone-500">{t("csv.csv_column")}</p>
                      <p className="font-medium truncate">{h}</p>
                    </div>
                    <select
                      value={mapping[h] || IGNORE}
                      onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                      className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:border-stone-900 min-w-[140px]"
                    >
                      <option value={IGNORE}>{t("csv.ignore")}</option>
                      {SCHEMA_FIELDS.map((field) => {
                        const takenBy = Object.entries(mapping).find(
                          ([col, mapped]) => mapped === field.key && col !== h
                        );
                        return (
                          <option key={field.key} value={field.key} disabled={Boolean(takenBy)}>
                            {fieldLabel(field, t)}{field.required ? " *" : ""}{takenBy ? ` (${t("csv.used_by", { column: takenBy[0] })})` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ))}
              </div>

              {unmappedRequired.length > 0 && (
                <div className="mt-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {t("csv.required_not_mapped", {
                      fields: unmappedRequired.map((field) => fieldLabel(field, t)).join(", "),
                    })}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
              <div className="p-6 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">{t("csv.preview")}</h2>
                  <p className="text-sm text-stone-500">
                    <span className="text-emerald-700 font-medium">{t("csv.valid", { count: preview.valid.length })}</span>
                    {" · "}
                    <span className="text-red-700 font-medium">{t("csv.invalid", { count: preview.invalid.length })}</span>
                  </p>
                </div>
                <button onClick={reset} className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1">
                  <Trash2 className="w-4 h-4" /> {t("csv.clear")}
                </button>
              </div>

              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr className="text-left text-xs uppercase text-stone-500">
                      <th className="px-4 py-2 font-medium">#</th>
                      <th className="px-4 py-2 font-medium">{t("csv.status")}</th>
                      {SCHEMA_FIELDS.map((field) => (
                        <th key={field.key} className="px-4 py-2 font-medium">{fieldLabel(field, t)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...preview.invalid, ...preview.valid].slice(0, 100).map(({ idx, product, errors }) => (
                      <tr key={idx} className="border-t border-stone-100 hover:bg-stone-50">
                        <td className="px-4 py-2 text-stone-400">{idx + 1}</td>
                        <td className="px-4 py-2">
                          {errors?.length ? (
                            <span className="inline-flex items-center gap-1 text-red-700" title={errors.join(", ")}>
                              <AlertCircle className="w-3.5 h-3.5" /> {t("csv.error")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="w-3.5 h-3.5" /> {t("csv.ready")}
                            </span>
                          )}
                        </td>
                        {SCHEMA_FIELDS.map((field) => (
                          <td key={field.key} className="px-4 py-2 text-stone-700 max-w-[180px] truncate">
                            {product[field.key] ?? <span className="text-stone-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 100 && (
                  <div className="px-4 py-3 text-xs text-stone-500 bg-stone-50 border-t">
                    {t("csv.showing_rows", { total: rows.length })}
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-4 bg-white border border-stone-200 rounded-2xl p-4 shadow-lg flex items-center justify-between">
              <div className="text-sm text-stone-600">
                {importing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("csv.importing", { done: progress.done, total: progress.total })}
                  </span>
                ) : (
                  <>
                    {t("csv.ready_to_import")} <strong>{preview.valid.length}</strong> {t("csv.products_to")} {" "}
                    <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded">{endpoint}</code>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={reset} disabled={importing} className="px-4 py-2 text-sm rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50">
                  {t("csv.cancel")}
                </button>
                <button
                  onClick={runImport}
                  disabled={importing || !preview.valid.length || unmappedRequired.length > 0}
                  className="px-4 py-2 text-sm rounded-lg bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {t("csv.import_products", { count: preview.valid.length })}
                </button>
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="space-y-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-6">
              <h2 className="font-semibold text-lg mb-4">{t("csv.complete")}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">{t("csv.succeeded")}</span>
                  </div>
                  <p className="text-3xl font-semibold mt-2 text-emerald-900">{results.success.length}</p>
                </div>
                <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">{t("csv.failed")}</span>
                  </div>
                  <p className="text-3xl font-semibold mt-2 text-red-900">{results.failed.length}</p>
                </div>
              </div>

              {results.failed.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium mb-2">{t("csv.failed_rows")}</h3>
                  <div className="max-h-64 overflow-auto border border-stone-200 rounded-lg divide-y divide-stone-100">
                    {results.failed.map(({ idx, product, error }) => (
                      <div key={idx} className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-stone-400">{t("csv.row", { number: idx + 1 })}</span>
                          <span className="font-medium truncate">{product.name || "—"}</span>
                        </div>
                        <p className="text-xs text-red-700 mt-1">{error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button onClick={reset} className="px-4 py-2 text-sm rounded-lg bg-stone-900 text-white hover:bg-stone-800">
                {t("csv.another")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
